import { Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { ShowMessage, useSnackbars } from '../../../hooks/snackbar.hook';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import { DecisionDeliberationDto } from '../../../services/dtos/decision-deliberation.dto';
import { FileDto } from '../../../services/dtos/file.dto';
import OpenFile from '../../action/open-file';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { DocumentsDto, DocumentsFormDto, SearchDocumentsDto } from '../../../services/dtos/documents.dto';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import DocumentsForm from '../../forms/documents/documents.form';
import { getDocumentContentFromService, searchDocumentsFromService } from '../../../services/documents.service';

/**
 * Ui de l'onglet documents
 * @constructor
 */
export default function DocumentsUi(
    props: Readonly<{
        mandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        fixedType?: string;
        title?: string;
        hideTypeFilter?: boolean;
    }>
) {
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Searching
    const [searching, setSearching] = useState<boolean>(false);
    // Gestion du dialog pour le signalement de changements
    const [file, setFile] = useState<FileDto>();
    //const label
    const labelDocument: string = 'doc-document';
    //Critères de recherche onglet decisions et deliberations
    const [searchDocuments, setSearchDocuments] = useState<SearchDocumentsDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandate.id,
        paramType: props.fixedType,
    });
    // result tableau decisions
    const [documents, setDocuments] = useState<ResultSearchDto<DocumentsDto>>();

    //Lancement de la recherche a chaque changement
    useEffect(() => {
        let isMounted = true;
        setSearching(true);

        const searchDocumentsDto: SearchDocumentsDto = {
            ...searchDocuments,
        };

        searchDocumentsFromService(searchDocumentsDto)
            .then(async result => {
                if (!isMounted) {
                    return;
                }

                if (result) {
                    await manageFile(result, String(props.mandate.id), showMessage);
                }
                setDocuments(result);
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des données');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchDocuments, props.mandate.id, showMessage]);

    // Permet de télécharger un document de la réunion
    const handlerDownloadFile = async (paramFile: FileDto) => {
        const resolvedFile = await loadFileContent(paramFile, showMessage);
        if (!resolvedFile?.content) {
            return;
        }
        const fileURL = globalThis.URL.createObjectURL(resolvedFile.content);
        globalThis.open(fileURL);
    };

    // Permet de visualiser un document de la réunion
    const handlerOpenFile = async (paramFile: FileDto | undefined) => {
        if (!paramFile) {
            setFile(undefined);
            return;
        }

        const resolvedFile = await loadFileContent(paramFile, showMessage);
        if (resolvedFile) {
            setFile(resolvedFile);
        }
    };

    // Mise en place du formulaire à renvoyer côté back
    const handlerSearch = (paramForm: DocumentsFormDto) => {
        const newSearchData: SearchDocumentsDto = {
            ...searchDocuments,
            paramType: props.fixedType ?? paramForm.type,
            paramName: paramForm.name,
            paramNumber: paramForm.number,
            paramSubCategory: paramForm.subcategory,
            paramYear: paramForm.year,
            paramMonth: paramForm.month,
            paramStartDate: paramForm.startDate,
            paramEndDate: paramForm.endDate,
        };
        setSearchDocuments(newSearchData);
    };

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchDocuments({
            ...searchDocuments,
            page: newPage,
        });
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchDocuments({
            ...searchDocuments,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    //handler sort
    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof DocumentsDto) => {
        setSearchDocuments({
            ...searchDocuments,
            order: order,
            sort: sort,
        });
    };

    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const defaultHeadCells: HeadCellDto<DecisionDeliberationDto, keyof DecisionDeliberationDto>[] = [
        { id: 'type', label: 'Type', sort: false, width: 15 },
        { id: 'subcategory', label: 'Sous-catégorie', sort: false, width: 15 },
        { id: 'name', label: 'Nom', sort: false, width: 40 },
        { id: 'date', label: 'Date', sort: true, width: 10 },
        { id: 'number', label: 'Numero', sort: false, width: 15 },
    ];
    const mapHeadCells: HeadCellDto<DecisionDeliberationDto, keyof DecisionDeliberationDto>[] = [
        { id: 'name', label: 'Nom', sort: false, width: 80 },
        { id: 'date', label: 'Date', sort: true, width: 20 },
    ];
    const headCells = props.fixedType === 'document_carte' ? mapHeadCells : defaultHeadCells;

    //Action à mettre dans le tableau, affichage et vue du document
    const action = (paramFile: DecisionDeliberationDto) => {
        return paramFile.finalDoc ? (
            <OpenFile
                handlerOpenFile={handlerOpenFile}
                handlerDownloadFile={handlerDownloadFile}
                file={paramFile.finalDoc}
            />
        ) : (
            <></>
        );
    };
    //Premet de conditionner l'affichage du tableau des decisions
    const showTable = () => {
        // Données pas encore chargées
        if (documents === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        // Données nulles
        if (documents?.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        } else {
            return (
                <>
                    <StyledTable
                        data={documents?.items}
                        searching={searching}
                        headCells={headCells}
                        action={action}
                        totalItems={documents?.totalItems}
                        page={searchDocuments?.page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeItemsPerPage}
                        itemsPerPage={searchDocuments?.itemsPerPage}
                        handlerSortBy={handlerSortBy}
                        order={searchDocuments?.order}
                        sort={searchDocuments?.sort}
                    ></StyledTable>
                </>
            );
        }
    };

    //Retourne la vue
    return file === undefined ? (
        <Grid container className={'tab-content documents-ui'} rowSpacing={1}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title'}>
                    {props.fixedType === 'document_carte' ? <LocationOnIcon /> : <InsertDriveFileIcon />}
                    {props.title ?? 'Documents'}
                </Typography>
            </Grid>
            <Grid item xs={12} md={1} />
            <Grid item xs={12} md={10}>
                <Stack>
                    {/*On affiche le formulaire*/}
                    <DocumentsForm
                        handlerSearch={handlerSearch}
                        searching={searching}
                        hideTypeField={props.hideTypeFilter}
                    />
                    {/* On affiche ensuite les documents*/}
                    {showTable()}
                </Stack>
            </Grid>
        </Grid>
    ) : (
        <Grid container className={'documents-ui'} sx={{ marginLeft: '30px' }} rowSpacing={1}>
            {/*On affiche le tableau pour les documents*/}
            <Grid
                item
                xs={12}
                sx={{
                    width: '80%',
                    display: 'flex',
                }}
            >
                {/* Bouton retour */}
                <Button
                    variant={'contained'}
                    sx={{
                        textAlign: 'center',
                        marginLeft: '10%',
                    }}
                    onClick={() => handlerOpenFile(undefined)}
                >
                    <FormatListBulletedIcon sx={{ marginRight: '5px' }} />
                    <Typography variant="body1" sx={{ color: '#fff' }}>
                        Afficher la liste des documents
                    </Typography>
                </Button>
            </Grid>
            {/*On affiche le pdf*/}
            <Grid
                item
                xs={12}
                sx={{
                    marginBottom: '20px',
                    height: '100vh',
                    width: '80%',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <object
                    aria-label={labelDocument}
                    data-testid={labelDocument}
                    className={labelDocument}
                    style={{ height: 'inherit', width: 'inherit' }}
                    data={file.data}
                ></object>
            </Grid>
        </Grid>
    );
}

/**
 * Méthode qui permet de récupérer le pdf correctement
 * @param result document
 * @param showMessage showMessage
 */
async function manageFile(result: ResultSearchDto<DocumentsDto>, mandateId: string, showMessage: ShowMessage) {
    for (const doc of result.items) {
        // Conversion du contenu en pdf si un doc est renvoyer format string
        if (doc.document?.file && doc.document?.fileName) {
            const docFile = formatDocumentFileData(doc.document?.file);
            const fileName = doc.document?.fileName;
            await fetch(docFile)
                .then(async pdf => {
                    // On transforme le contenu en blob
                    const content = await pdf.blob();
                    doc.finalDoc = { name: fileName, data: docFile, content: content };
                })
                .catch(error => {
                    console.error('Erreur lors de la conversion du document.');
                    showAxiosMessageError(error, showMessage);
                });
        } else if (doc.document?.fileName && doc.document?.gouvernanceDocumentId) {
            doc.finalDoc = {
                name: doc.document.fileName,
                data: '',
                mandateId,
                gouvernanceDocumentId: doc.document.gouvernanceDocumentId,
                documentId: doc.document.documentId,
            };
        }
    }
}

function formatDocumentFileData(fileData: string): string {
    if (fileData.startsWith('data:') || fileData.startsWith('blob:') || fileData.startsWith('http')) {
        return fileData;
    }

    return `data:application/pdf;base64,${fileData}`;
}

async function loadFileContent(paramFile: FileDto, showMessage: ShowMessage): Promise<FileDto | undefined> {
    if (paramFile.content) {
        return paramFile;
    }

    if (!paramFile.mandateId || !paramFile.gouvernanceDocumentId) {
        return paramFile;
    }

    try {
        const content = await getDocumentContentFromService(paramFile.mandateId, paramFile.gouvernanceDocumentId);
        const data = globalThis.URL.createObjectURL(content);

        return {
            ...paramFile,
            content,
            data,
        };
    } catch (error) {
        console.error('Erreur lors de la récupération du contenu du document.');
        showAxiosMessageError(error as Error, showMessage);
        return undefined;
    }
}
